import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowRight, FiStar, FiHeart, FiZap, FiSmile, FiUsers, FiCpu, FiGlobe, FiPlay, FiCheck, FiTrendingUp } = FiIcons;

const GAME_TEASERS = [
  {
    category: 'Math & Logic',
    emoji: '🧮',
    color: 'from-green-400 to-emerald-500',
    games: ['Counting Safari', 'Math Jungle', 'Multiplication Island', 'Logic Puzzles']
  },
  {
    category: 'Science & Nature',
    emoji: '🔬',
    color: 'from-blue-400 to-cyan-500',
    games: ['Science Lab', 'Color Explorer', 'Shape Adventure']
  },
  {
    category: 'Social-Emotional Learning',
    emoji: '❤️',
    color: 'from-pink-400 to-rose-500',
    games: ['Feelings Faces', 'Kindness Post Office', 'Empathy Swap', 'Friendship Bridge']
  },
  {
    category: 'Reading & Language',
    emoji: '📖',
    color: 'from-purple-400 to-violet-500',
    games: ['Reading Quest', 'Memory Match']
  }
];

const SEL_HIGHLIGHTS = [
  { icon: '🎭', title: 'Emotion Recognition', desc: 'Learn to identify feelings' },
  { icon: '🤝', title: 'Empathy Building', desc: 'Walk in others\' shoes' },
  { icon: '🧘', title: 'Self-Regulation', desc: 'Calm-down techniques' },
  { icon: '💝', title: 'Kindness', desc: 'Spread positivity' }
];

function WelcomeScreen() {
  const navigate = useNavigate();
  const [showTeasers, setShowTeasers] = useState(true);

  return (
    <motion.div 
      initial={{ opacity: 0 }} 
      animate={{ opacity: 1 }} 
      exit={{ opacity: 0 }}
      className="min-h-screen bg-gradient-to-br from-jungle-100 via-safari-50 to-jungle-200 overflow-y-auto"
    >
      {/* Hero Section */}
      <div className="relative min-h-screen flex flex-col items-center justify-center p-4 pb-20">
        {/* Background Decorations */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div animate={{ y: [0, -20, 0] }} transition={{ duration: 4, repeat: Infinity }} className="absolute top-10 left-10 text-6xl opacity-20">🌿</motion.div>
          <motion.div animate={{ y: [0, -15, 0] }} transition={{ duration: 3, repeat: Infinity, delay: 0.5 }} className="absolute top-20 right-20 text-5xl opacity-20">🦋</motion.div>
          <motion.div animate={{ y: [0, -25, 0] }} transition={{ duration: 5, repeat: Infinity, delay: 1 }} className="absolute bottom-40 left-20 text-7xl opacity-20">🦁</motion.div>
          <motion.div animate={{ y: [0, -18, 0] }} transition={{ duration: 4, repeat: Infinity, delay: 1.5 }} className="absolute bottom-20 right-10 text-6xl opacity-20">🌟</motion.div>
        </div>

        <motion.div 
          initial={{ y: -50, opacity: 0 }} 
          animate={{ y: 0, opacity: 1 }} 
          transition={{ delay: 0.2 }}
          className="text-center mb-8 relative z-10"
        >
          <motion.div 
            animate={{ rotate: [0, 10, -10, 0] }} 
            transition={{ duration: 4, repeat: Infinity }}
            className="text-8xl mb-4 drop-shadow-2xl"
          >
            🦁
          </motion.div>
          <h1 className="text-5xl md:text-6xl font-kid text-jungle-700 drop-shadow-lg mb-4">IntelliJungle</h1>
          <p className="text-jungle-600 font-comic text-xl md:text-2xl max-w-lg mx-auto">
            Where Learning Meets Adventure!
          </p>
        </motion.div>

        <AnimatePresence mode="wait">
          {showTeasers ? (
            <motion.div 
              key="teasers"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="w-full max-w-5xl mx-auto relative z-10"
            >
              {/* Game Teasers Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                {GAME_TEASERS.map((section, idx) => (
                  <motion.div
                    key={section.category}
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ delay: 0.3 + idx * 0.1 }}
                    whileHover={{ scale: 1.05, y: -5 }}
                    className={`bg-gradient-to-br ${section.color} rounded-3xl p-5 shadow-xl text-white border-4 border-white/30`}
                  >
                    <div className="text-4xl mb-3">{section.emoji}</div>
                    <h3 className="font-kid text-lg mb-2">{section.category}</h3>
                    <ul className="space-y-1">
                      {section.games.map((game, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm font-comic opacity-90">
                          <SafeIcon icon={FiCheck} className="text-xs" /> {game}
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                ))}
              </div>

              {/* SEL Highlights */}
              <motion.div 
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.7 }}
                className="bg-white/80 backdrop-blur-sm rounded-3xl p-6 shadow-xl border-4 border-safari-200 mb-8"
              >
                <h2 className="text-center font-kid text-2xl text-jungle-700 mb-4 flex items-center justify-center gap-2">
                  <SafeIcon icon={FiHeart} className="text-rose-500" /> New: Social-Emotional Learning Games
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {SEL_HIGHLIGHTS.map((item, i) => (
                    <div key={i} className="text-center">
                      <div className="text-4xl mb-2">{item.icon}</div>
                      <p className="font-kid text-sm text-jungle-700">{item.title}</p>
                      <p className="font-comic text-xs text-gray-500">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </motion.div>

              <motion.div 
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.9 }}
                className="flex flex-col md:flex-row gap-4 justify-center items-center"
              >
                <button 
                  onClick={() => setShowTeasers(false)}
                  className="bg-gradient-to-r from-jungle-500 to-jungle-600 text-white px-8 py-4 rounded-[2rem] font-kid text-xl shadow-2xl hover:scale-105 transition-all flex items-center gap-3 border-b-8 border-jungle-700"
                >
                  Start Your Adventure <SafeIcon icon={FiArrowRight} />
                </button>
                <button 
                  onClick={() => navigate('/login')}
                  className="bg-white text-jungle-600 px-8 py-4 rounded-[2rem] font-kid text-xl shadow-xl hover:bg-jungle-50 transition-all border-4 border-jungle-200"
                >
                  I Already Have an Account
                </button>
              </motion.div>

              {/* Stats */}
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.1 }}
                className="flex justify-center gap-8 mt-8 text-jungle-600 font-comic"
              >
                <div className="flex items-center gap-2"><SafeIcon icon={FiPlay} className="text-jungle-500" /> 20+ Games</div>
                <div className="flex items-center gap-2"><SafeIcon icon={FiUsers} className="text-jungle-500" /> Ages 4-12</div>
                <div className="flex items-center gap-2"><SafeIcon icon={FiCpu} className="text-jungle-500" /> STEM + SEL</div>
              </motion.div>
            </motion.div>
          ) : (
            <RegistrationModeSelector onBack={() => setShowTeasers(true)} />
          )}
        </AnimatePresence>

        {/* Footer decoration */}
        <div className="absolute bottom-0 left-0 right-0 flex justify-center gap-4 pb-6 opacity-50">
          <SafeIcon icon={FiStar} className="text-yellow-400 text-2xl animate-pulse" />
          <SafeIcon icon={FiHeart} className="text-rose-400 text-2xl animate-pulse" style={{ animationDelay: '0.3s' }} />
          <SafeIcon icon={FiZap} className="text-blue-400 text-2xl animate-pulse" style={{ animationDelay: '0.6s' }} />
        </div>
      </div>
    </motion.div>
  );
}

function RegistrationModeSelector({ onBack }) {
  const navigate = useNavigate();
  const [mode, setMode] = useState(null);

  if (mode === 'kid') {
    return <KidRegistration onBack={() => setMode(null)} />;
  }
  if (mode === 'adult') {
    return <AdultRegistration onBack={() => setMode(null)} />;
  }

  return (
    <motion.div 
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      className="bg-white rounded-[3rem] p-8 md:p-12 shadow-2xl border-4 border-safari-200 max-w-2xl mx-auto"
    >
      <button 
        onClick={onBack}
        className="mb-6 flex items-center gap-2 text-jungle-600 font-comic hover:text-jungle-800"
      >
        ← Back to Games
      </button>

      <h2 className="text-3xl font-kid text-jungle-700 text-center mb-2">Who's Joining?</h2>
      <p className="font-comic text-jungle-600 text-center mb-8">Choose how you want to register</p>

      <div className="grid md:grid-cols-2 gap-6">
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setMode('kid')}
          className="bg-gradient-to-br from-green-400 to-green-500 rounded-3xl p-6 text-white shadow-xl border-4 border-white"
        >
          <div className="text-5xl mb-4">👧👦</div>
          <h3 className="font-kid text-2xl mb-2">I'm a Kid!</h3>
          <p className="font-comic text-sm opacity-90">Quick signup with just your name, birthday, and password</p>
          <div className="mt-4 flex items-center gap-2 text-sm font-comic">
            <SafeIcon icon={FiCheck} /> No email needed!
          </div>
        </motion.button>

        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setMode('adult')}
          className="bg-gradient-to-br from-blue-400 to-blue-500 rounded-3xl p-6 text-white shadow-xl border-4 border-white"
        >
          <div className="text-5xl mb-4">👨‍👩‍👧‍👦</div>
          <h3 className="font-kid text-2xl mb-2">I'm a Parent</h3>
          <p className="font-comic text-sm opacity-90">Register multiple kids with parent email</p>
          <div className="mt-4 flex items-center gap-2 text-sm font-comic">
            <SafeIcon icon={FiTrendingUp} /> Track progress!
          </div>
        </motion.button>
      </div>
    </motion.div>
  );
}

function KidRegistration({ onBack }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({ name: '', dob: '', password: '', confirmPassword: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const calculateAge = (dob) => {
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const handleSubmit = async () => {
    if (formData.password !== formData.confirmPassword) {
      setError("Passwords don't match!");
      return;
    }
    if (formData.password.length < 4) {
      setError("Password must be at least 4 characters");
      return;
    }

    setLoading(true);
    setError('');
    
    // Simulate registration - would connect to actual API
    setTimeout(() => {
      const age = calculateAge(formData.dob);
      const mockUser = {
        id: crypto.randomUUID(),
        name: formData.name,
        age: age,
        isKid: true
      };
      localStorage.setItem('ncb_user', JSON.stringify(mockUser));
      navigate('/dashboard');
    }, 1500);
  };

  return (
    <motion.div 
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -50 }}
      className="bg-white rounded-[3rem] p-8 md:p-10 shadow-2xl border-4 border-safari-200 max-w-md mx-auto"
    >
      <button onClick={onBack} className="mb-4 text-jungle-600 font-comic">← Back</button>
      
      <div className="flex items-center justify-center mb-6">
        <div className="text-5xl">👧</div>
      </div>
      
      <h2 className="text-2xl font-kid text-jungle-700 text-center mb-6">Kid Explorer Signup</h2>

      {/* Progress Steps */}
      <div className="flex justify-center gap-2 mb-8">
        {[1, 2, 3].map((i) => (
          <div key={i} className={`w-8 h-8 rounded-full flex items-center justify-center font-kid text-sm ${step >= i ? 'bg-jungle-500 text-white' : 'bg-gray-200 text-gray-400'}`}>
            {i}
          </div>
        ))}
      </div>

      {error && (
        <div className="bg-red-100 border-2 border-red-300 text-red-600 p-3 rounded-xl mb-4 font-comic text-sm text-center">
          {error}
        </div>
      )}

      {step === 1 && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <label className="block font-comic text-jungle-700 mb-2">What's your name?</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="Enter your name"
            className="w-full p-4 bg-jungle-50 border-2 border-jungle-200 rounded-2xl font-comic mb-6 focus:outline-none focus:border-jungle-400"
          />
          <button
            onClick={() => formData.name && setStep(2)}
            disabled={!formData.name}
            className="w-full bg-jungle-500 text-white p-4 rounded-2xl font-kid text-lg disabled:opacity-50"
          >
            Next! →
          </button>
        </motion.div>
      )}

      {step === 2 && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <label className="block font-comic text-jungle-700 mb-2">When is your birthday?</label>
          <input
            type="date"
            value={formData.dob}
            onChange={(e) => setFormData({ ...formData, dob: e.target.value })}
            className="w-full p-4 bg-jungle-50 border-2 border-jungle-200 rounded-2xl font-comic mb-6 focus:outline-none focus:border-jungle-400"
          />
          <div className="flex gap-3">
            <button onClick={() => setStep(1)} className="flex-1 bg-gray-200 text-gray-700 p-4 rounded-2xl font-kid">← Back</button>
            <button
              onClick={() => formData.dob && setStep(3)}
              disabled={!formData.dob}
              className="flex-1 bg-jungle-500 text-white p-4 rounded-2xl font-kid disabled:opacity-50"
            >
              Next! →
            </button>
          </div>
        </motion.div>
      )}

      {step === 3 && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <label className="block font-comic text-jungle-700 mb-2">Create a secret password</label>
          <input
            type="password"
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            placeholder="Enter password"
            className="w-full p-4 bg-jungle-50 border-2 border-jungle-200 rounded-2xl font-comic mb-4 focus:outline-none focus:border-jungle-400"
          />
          <input
            type="password"
            value={formData.confirmPassword}
            onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
            placeholder="Confirm password"
            className="w-full p-4 bg-jungle-50 border-2 border-jungle-200 rounded-2xl font-comic mb-6 focus:outline-none focus:border-jungle-400"
          />
          <div className="flex gap-3">
            <button onClick={() => setStep(2)} className="flex-1 bg-gray-200 text-gray-700 p-4 rounded-2xl font-kid">← Back</button>
            <button
              onClick={handleSubmit}
              disabled={loading || !formData.password}
              className="flex-1 bg-jungle-500 text-white p-4 rounded-2xl font-kid disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? 'Creating...' : <>Start Adventure! 🚀</>}
            </button>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
}

function AdultRegistration({ onBack }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({ 
    email: '', 
    password: '', 
    confirmPassword: '',
    kids: [{ name: '', dob: '', kidPassword: '' }]
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const addKid = () => {
    setFormData({ ...formData, kids: [...formData.kids, { name: '', dob: '', kidPassword: '' }] });
  };

  const updateKid = (index, field, value) => {
    const newKids = [...formData.kids];
    newKids[index][field] = value;
    setFormData({ ...formData, kids: newKids });
  };

  const removeKid = (index) => {
    if (formData.kids.length > 1) {
      setFormData({ ...formData, kids: formData.kids.filter((_, i) => i !== index) });
    }
  };

  const handleSubmit = async () => {
    if (formData.password !== formData.confirmPassword) {
      setError("Passwords don't match!");
      return;
    }
    
    const incompleteKid = formData.kids.find(k => !k.name || !k.dob || !k.kidPassword);
    if (incompleteKid) {
      setError("Please complete all kid information");
      return;
    }

    setLoading(true);
    setTimeout(() => {
      const mockUser = {
        id: crypto.randomUUID(),
        email: formData.email,
        isParent: true,
        kids: formData.kids
      };
      localStorage.setItem('ncb_user', JSON.stringify(mockUser));
      navigate('/parent');
    }, 1500);
  };

  return (
    <motion.div 
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -50 }}
      className="bg-white rounded-[3rem] p-8 md:p-10 shadow-2xl border-4 border-safari-200 max-w-2xl mx-auto max-h-[90vh] overflow-y-auto"
    >
      <button onClick={onBack} className="mb-4 text-jungle-600 font-comic">← Back</button>
      
      <div className="flex items-center justify-center mb-6">
        <div className="text-5xl">👨‍👩‍👧‍👦</div>
      </div>
      
      <h2 className="text-2xl font-kid text-jungle-700 text-center mb-2">Parent Registration</h2>
      <p className="font-comic text-jungle-600 text-center mb-6">Set up accounts for your children</p>

      {/* Progress */}
      <div className="flex justify-center gap-2 mb-8">
        {[1, 2].map((i) => (
          <div key={i} className={`w-8 h-8 rounded-full flex items-center justify-center font-kid text-sm ${step >= i ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-400'}`}>
            {i}
          </div>
        ))}
      </div>

      {error && (
        <div className="bg-red-100 border-2 border-red-300 text-red-600 p-3 rounded-xl mb-4 font-comic text-sm text-center">
          {error}
        </div>
      )}

      {step === 1 && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
          <div>
            <label className="block font-comic text-jungle-700 mb-2">Parent Email</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="your@email.com"
              className="w-full p-4 bg-blue-50 border-2 border-blue-200 rounded-2xl font-comic focus:outline-none focus:border-blue-400"
            />
          </div>
          <div>
            <label className="block font-comic text-jungle-700 mb-2">Create Password</label>
            <input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              placeholder="Password"
              className="w-full p-4 bg-blue-50 border-2 border-blue-200 rounded-2xl font-comic focus:outline-none focus:border-blue-400"
            />
          </div>
          <div>
            <input
              type="password"
              value={formData.confirmPassword}
              onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
              placeholder="Confirm Password"
              className="w-full p-4 bg-blue-50 border-2 border-blue-200 rounded-2xl font-comic focus:outline-none focus:border-blue-400"
            />
          </div>
          <button
            onClick={() => formData.email && formData.password && setStep(2)}
            disabled={!formData.email || !formData.password}
            className="w-full bg-blue-500 text-white p-4 rounded-2xl font-kid text-lg disabled:opacity-50"
          >
            Next: Add Kids →
          </button>
        </motion.div>
      )}

      {step === 2 && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-kid text-xl text-jungle-700">Kid Accounts ({formData.kids.length})</h3>
            <button onClick={addKid} className="bg-green-500 text-white px-4 py-2 rounded-xl font-comic text-sm hover:bg-green-600">
              + Add Another Kid
            </button>
          </div>

          <div className="space-y-4 max-h-64 overflow-y-auto p-2">
            {formData.kids.map((kid, idx) => (
              <div key={idx} className="bg-safari-50 rounded-2xl p-4 border-2 border-safari-200">
                <div className="flex justify-between items-center mb-3">
                  <span className="font-kid text-jungle-700">Kid #{idx + 1}</span>
                  {formData.kids.length > 1 && (
                    <button onClick={() => removeKid(idx)} className="text-red-500 text-sm font-comic">Remove</button>
                  )}
                </div>
                <div className="grid gap-3">
                  <input
                    type="text"
                    value={kid.name}
                    onChange={(e) => updateKid(idx, 'name', e.target.value)}
                    placeholder="Kid's Name"
                    className="w-full p-3 bg-white border-2 border-safari-200 rounded-xl font-comic"
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="date"
                      value={kid.dob}
                      onChange={(e) => updateKid(idx, 'dob', e.target.value)}
                      className="w-full p-3 bg-white border-2 border-safari-200 rounded-xl font-comic"
                    />
                    <input
                      type="password"
                      value={kid.kidPassword}
                      onChange={(e) => updateKid(idx, 'kidPassword', e.target.value)}
                      placeholder="Kid's Password"
                      className="w-full p-3 bg-white border-2 border-safari-200 rounded-xl font-comic"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="flex gap-3 mt-6">
            <button onClick={() => setStep(1)} className="flex-1 bg-gray-200 text-gray-700 p-4 rounded-2xl font-kid">← Back</button>
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="flex-1 bg-blue-500 text-white p-4 rounded-2xl font-kid disabled:opacity-50"
            >
              {loading ? 'Creating...' : 'Complete Setup! 🎉'}
            </button>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
}

export default WelcomeScreen;